import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Syringe, Plus, Trash2, UserPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY_CONTACT = { first_name: "", last_name: "", phone: "", email: "" };

export default function VaccineDriveRequest() {
  const [form, setForm] = useState({ address: "", date: "", time: "", staff_count: "", residents_count: "", staff_flu: "", staff_covid: "", staff_rsv: "", residents_flu: "", residents_covid: "", residents_rsv: "" });
  const [contacts, setContacts] = useState([{ ...EMPTY_CONTACT }]);
  const [createAccount, setCreateAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setC = (i, k) => (e) => setContacts((cs) => cs.map((c, idx) => (idx === i ? { ...c, [k]: e.target.value } : c)));
  const addContact = () => setContacts((cs) => [...cs, { ...EMPTY_CONTACT }]);
  const removeContact = (i) => setContacts((cs) => cs.filter((_, idx) => idx !== i));

  async function submit(e) {
    e.preventDefault();
    if (!form.address || !form.date || !form.time) return toast.error("Address, date and time are required.");
    if (contacts.some((c) => !c.first_name || !c.last_name || !c.email)) return toast.error("Each contact needs a first name, last name and email.");
    setLoading(true);
    try {
      await api.post("/vaccine-drive/requests", {
        ...form,
        staff_count: Number(form.staff_count) || 0,
        residents_count: Number(form.residents_count) || 0,
        staff_flu: Number(form.staff_flu) || 0,
        staff_covid: Number(form.staff_covid) || 0,
        staff_rsv: Number(form.staff_rsv) || 0,
        residents_flu: Number(form.residents_flu) || 0,
        residents_covid: Number(form.residents_covid) || 0,
        residents_rsv: Number(form.residents_rsv) || 0,
        contacts,
        create_account: createAccount,
      });
      toast.success(createAccount ? "Request submitted! Check your email for a login code to finish setting up your Health Partner account." : "Vaccine drive request submitted! We'll confirm shortly.");
      setForm({ address: "", date: "", time: "", staff_count: "", residents_count: "", staff_flu: "", staff_covid: "", staff_rsv: "", residents_flu: "", residents_covid: "", residents_rsv: "" });
      setContacts([{ ...EMPTY_CONTACT }]);
      setCreateAccount(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-16" data-testid="vaccine-drive-page">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><Syringe className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Request a Vaccine Drive</h1>
          <p className="text-sm text-muted-foreground">On-site vaccinations for your facility, staff and residents. Tell us the details and we'll confirm.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6 rounded-2xl border border-border bg-card p-6 sm:p-8">
        <div className="space-y-2">
          <Label htmlFor="address">Address of the drive</Label>
          <Input id="address" data-testid="drive-address" value={form.address} onChange={set("address")} required />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2"><Label htmlFor="date">Date</Label><Input id="date" type="date" data-testid="drive-date" value={form.date} onChange={set("date")} required /></div>
          <div className="space-y-2"><Label htmlFor="time">Time</Label><Input id="time" type="time" data-testid="drive-time" value={form.time} onChange={set("time")} required /></div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Contact person(s)</Label>
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addContact} data-testid="add-contact-btn"><Plus className="mr-1 h-4 w-4" /> Add a contact person</Button>
          </div>
          {contacts.map((c, i) => (
            <div key={i} className="rounded-xl border border-border/60 p-4" data-testid={`contact-block-${i}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">Contact {i + 1}</span>
                {contacts.length > 1 && <button type="button" onClick={() => removeContact(i)} data-testid={`remove-contact-${i}`} className="text-destructive"><Trash2 className="h-4 w-4" /></button>}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input placeholder="First name" data-testid={`contact-first-${i}`} value={c.first_name} onChange={setC(i, "first_name")} required />
                <Input placeholder="Last name" data-testid={`contact-last-${i}`} value={c.last_name} onChange={setC(i, "last_name")} required />
                <Input placeholder="Phone number" data-testid={`contact-phone-${i}`} value={c.phone} onChange={setC(i, "phone")} />
                <Input placeholder="Email" type="email" data-testid={`contact-email-${i}`} value={c.email} onChange={setC(i, "email")} required />
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-3 rounded-xl border border-border/60 p-4">
            <Label className="text-sm font-semibold">Staff</Label>
            <div className="space-y-1.5"><Label htmlFor="staff" className="text-xs">How many requested a vaccination?</Label><Input id="staff" type="number" min="0" data-testid="drive-staff-count" value={form.staff_count} onChange={set("staff_count")} placeholder="e.g. 40" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1"><Label className="text-xs">Flu</Label><Input type="number" min="0" data-testid="drive-staff-flu" value={form.staff_flu} onChange={set("staff_flu")} /></div>
              <div className="space-y-1"><Label className="text-xs">COVID</Label><Input type="number" min="0" data-testid="drive-staff-covid" value={form.staff_covid} onChange={set("staff_covid")} /></div>
              <div className="space-y-1"><Label className="text-xs">RSV</Label><Input type="number" min="0" data-testid="drive-staff-rsv" value={form.staff_rsv} onChange={set("staff_rsv")} /></div>
            </div>
          </div>
          <div className="space-y-3 rounded-xl border border-border/60 p-4">
            <Label className="text-sm font-semibold">Residents</Label>
            <div className="space-y-1.5"><Label htmlFor="residents" className="text-xs">How many requested a vaccination?</Label><Input id="residents" type="number" min="0" data-testid="drive-residents-count" value={form.residents_count} onChange={set("residents_count")} placeholder="e.g. 120" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1"><Label className="text-xs">Flu</Label><Input type="number" min="0" data-testid="drive-residents-flu" value={form.residents_flu} onChange={set("residents_flu")} /></div>
              <div className="space-y-1"><Label className="text-xs">COVID</Label><Input type="number" min="0" data-testid="drive-residents-covid" value={form.residents_covid} onChange={set("residents_covid")} /></div>
              <div className="space-y-1"><Label className="text-xs">RSV</Label><Input type="number" min="0" data-testid="drive-residents-rsv" value={form.residents_rsv} onChange={set("residents_rsv")} /></div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={createAccount} onCheckedChange={(v) => setCreateAccount(!!v)} data-testid="drive-create-account" className="mt-0.5" />
            <span className="text-sm">
              <span className="flex items-center gap-1 font-semibold"><UserPlus className="h-4 w-4 text-primary" /> Create a Health Partner account now</span>
              <span className="text-muted-foreground">Optional — this makes managing this and future drives (rosters, insurance, scheduling) much quicker. We'll email a login code to your first contact.</span>
            </span>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={loading} data-testid="drive-submit" className="rounded-full sm:px-10">{loading ? "Submitting..." : "Submit Request"}</Button>
          <Link to="/health-partners" className="text-sm font-medium text-primary hover:underline" data-testid="drive-to-partners">Already a Health Partner? Sign in</Link>
        </div>
      </form>
    </div>
  );
}
